import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as loginApi, getOrgBySlug } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [brandedOrg, setBrandedOrg] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Clear stale auth data when visiting login page
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isAuthenticated) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('selectedOrg');
      sessionStorage.removeItem('operatingMode');
    }
  }, [isAuthenticated]);
  
  // Check for org branding parameter
  useEffect(() => {
    const orgSlug = searchParams.get('org');
    if (orgSlug) {
      fetchOrgBranding(orgSlug);
    }
  }, [searchParams]);
  
  const fetchOrgBranding = async (slug) => {
    try {
      const response = await getOrgBySlug(slug);
      setBrandedOrg(response.data);
    } catch (error) {
      console.log('No branded org found for slug:', slug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await loginApi({
        email: formData.email,
        password: formData.password,
      });
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
      // Redirect brokers to their dashboard
      if (response.data.user?.role === 'broker') {
        navigate('/broker');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Clear any stale auth data on login failure
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]?.msg || 'Authentication failed');
      } else {
        toast.error('Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-16 xl:px-24 bg-white">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="mb-10">
            {brandedOrg?.logo_url ? (
              <img 
                src={brandedOrg.logo_url.startsWith('/api') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${brandedOrg.logo_url}` 
                  : brandedOrg.logo_url
                }
                alt={brandedOrg.name}
                className="h-12 w-auto"
                data-testid="branded-org-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
                alt="Players Health" 
                className="h-10 w-auto"
              />
            )}
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">
              Welcome back
            </h1>
            <p className="text-gray-500">
              {brandedOrg 
                ? `Enter your credentials to access ${brandedOrg.name}`
                : 'Enter your credentials to access the admin portal'}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="admin@playershealth.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Password
                </Label>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  onClick={() => toast.info('Contact your administrator to reset your password')}
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-base mt-2"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/login-hero.png')`,
          }}
        />
        
        {/* Gradient Overlay - lighter to let the image show through */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/40 via-purple-600/30 to-transparent" />
        
        {/* Content */}
        <div className="relative h-full flex flex-col justify-end p-12 xl:p-16">
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
              Welcome to Players Health
            </h2>
            <p className="text-white/90 text-lg leading-relaxed space-y-2">
              Our mission is clear: create the safest and most accessible environment for athletes to play the sports they love.
            </p>
            <p className="text-white/90 text-lg leading-relaxed mt-4">
              This platform is your tool to make that mission a reality.
            </p>
            <p className="text-white/90 text-lg leading-relaxed mt-4 font-medium">
              Thank you for joining the Players Health movement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
